/**
 * Phase 3E-9L — Central display-label resolver for user-facing network names.
 *
 * DO NOT rename backend canonical keys ("esso", "pipeline", "ta_petro",
 * "petro_pass"). This helper is DISPLAY-LAYER ONLY.
 *
 * Canonical mapping (operator brief):
 *   esso       + CA → "Esso CAD/L"
 *   esso       + US → "Esso USD/GAL"          (rare — most US is ta_petro)
 *   pipeline   + CA → "Pipeline CAD/L"
 *   ta_petro   + *  → "Esso USD/GAL"          (business rebrand; internal key
 *                                              stays "ta_petro" for continuity)
 *   petro_pass + CA → "Petro-Pass Canada CAD/L"
 *   petro_pass + US → "Petro-Pass USA USD/GAL"
 *
 * Alias inputs (some routes/UIs use the flattened "petro_pass_us" /
 * "petro_pass_ca" convention) are also accepted so callers don't need to
 * pre-split.
 */

/** Returns the user-facing network label. Country is inferred from network
 *  alias when omitted. Returns the raw network string if nothing matches. */
export function getNetworkDisplayName(network, country) {
  const n = String(network || "").toLowerCase();
  const c = String(country || "").toUpperCase();

  if (n === "petro_pass_ca") return "Petro-Pass Canada CAD/L";
  if (n === "petro_pass_us") return "Petro-Pass USA USD/GAL";

  if (n === "petro_pass") {
    if (c === "CA") return "Petro-Pass Canada CAD/L";
    if (c === "US") return "Petro-Pass USA USD/GAL";
    return "Petro-Pass";
  }
  if (n === "ta_petro") return "Esso USD/GAL";
  if (n === "esso") {
    if (c === "US") return "Esso USD/GAL";
    return "Esso CAD/L";
  }
  if (n === "pipeline") return "Pipeline CAD/L";
  return network || "";
}

/** Country inferred from network. Returns "CA" | "US" | "" (unknown/mixed). */
export function getNetworkCountry(network, country) {
  const n = String(network || "").toLowerCase();
  if (country) return String(country).toUpperCase();
  if (n === "petro_pass_us" || n === "ta_petro") return "US";
  if (n === "petro_pass_ca" || n === "esso" || n === "pipeline") return "CA";
  return "";
}

/** The price-column header string ("PRICE/GAL." vs "PRICE/LTR."). */
export function getNetworkPriceHeader(network, country) {
  return getNetworkCountry(network, country) === "US" ? "PRICE/GAL." : "PRICE/LTR.";
}

/** Currency + UOM strings ("USD"/"GAL" vs "CAD"/"L"). */
export function getNetworkCurrencyUom(network, country) {
  return getNetworkCountry(network, country) === "US"
    ? { currency: "USD", uom: "GAL" }
    : { currency: "CAD", uom: "L" };
}

/** For maps + drilldowns — "Province" (CA) vs "State" (US) vs "Jurisdiction" (mixed). */
export function getJurisdictionLabel(network, country) {
  const cc = getNetworkCountry(network, country);
  if (cc === "CA") return "Province";
  if (cc === "US") return "State";
  return "Jurisdiction";
}

/** Every canonical mapping — used by isolated tests to prove coverage. */
export const CANONICAL_LABELS = {
  "esso|CA": "Esso CAD/L",
  "esso|US": "Esso USD/GAL",
  "pipeline|CA": "Pipeline CAD/L",
  "ta_petro|US": "Esso USD/GAL",
  "petro_pass|CA": "Petro-Pass Canada CAD/L",
  "petro_pass|US": "Petro-Pass USA USD/GAL",
};
